import * as i1$1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { EndpointUrl } from '@ztarmobile/zwp-service';
import { take, map, flatMap, timeout } from 'rxjs/operators';
import * as i1 from '@ztarmobile/zwp-services-auth';
import * as L from 'leaflet';
import { ReplaySubject } from 'rxjs';
import * as i1$2 from '@angular/router';
import * as i2 from '@angular/common';

class I18nService {
    constructor(endpointUrl, http) {
        this.endpointUrl = endpointUrl;
        this.http = http;
    }
    changeLanguage(code) {
        return new Promise((resolve, reject) => {
            this.http.put(this.endpointUrl + '/api/language/:langCode' + code, {}).pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    currentLanguage() {
        return new Promise((resolve, reject) => {
            this.http.get(this.endpointUrl + '/api/language').pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    resetLanguage() {
        return new Promise((resolve, reject) => {
            this.http.delete(this.endpointUrl + '/api/language').pipe(take(1))
                .subscribe((response) => resolve(response), (error) => this.http.handlePromiseError(error, reject));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: I18nService, deps: [{ token: EndpointUrl }, { token: i1.AuthHttp }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: I18nService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: I18nService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [EndpointUrl]
                }] }, { type: i1.AuthHttp }] });

class Location {
}
class BingLayer extends L.TileLayer {
    constructor(urlTemplate, options) {
        super(urlTemplate, options);
        this.url = urlTemplate;
    }
    getTileUrl(tilePoint) {
        return L.Util.template(this.url, {
            q: this._quadKey(tilePoint.x, tilePoint.y, tilePoint.z)
        });
    }
    _quadKey(x, y, z) {
        const quadKey = [];
        for (let i = z; i > 0; i--) {
            let digit = 0;
            const mask = 1 << (i - 1);
            if ((x & mask) != 0) {
                digit++;
            }
            if ((y & mask) != 0) {
                digit++;
                digit++;
            }
            quadKey.push(digit);
        }
        return quadKey.join('');
    }
}

class GeocodingService {
    constructor(httpClient) {
        this.httpClient = httpClient;
    }
    geocode(address, key) {
        return this.httpClient
            .get('https://maps.googleapis.com/maps/api/geocode/json?address=' + encodeURIComponent(address) + '&key=' + key)
            .pipe(map((result) => {
            if (result.status !== 'OK') {
                throw new Error('unable to geocode address');
            }
            const location = new Location();
            location.address = result.results[0].formatted_address;
            location.latitude = result.results[0].geometry.location.lat;
            location.longitude = result.results[0].geometry.location.lng;
            const viewPort = result.results[0].geometry.viewport;
            location.viewBounds = L.latLngBounds({
                lat: viewPort.southwest.lat,
                lng: viewPort.southwest.lng
            }, {
                lat: viewPort.northeast.lat,
                lng: viewPort.northeast.lng
            });
            return location;
        }));
    }
    getCurrentLocation() {
        const location = new ReplaySubject(1);
        navigator.geolocation.getCurrentPosition((position) => {
            const html5Location = new Location();
            html5Location.latitude = position.coords.latitude;
            html5Location.longitude = position.coords.longitude;
            // can be filled if needed https://developers.google.com/maps/documentation/geocoding/intro#ReverseGeocoding
            html5Location.address = '';
            location.next(html5Location);
        }, (geoError) => {
            console.warn(`GeoLocation error: ${geoError.message} with error code ${geoError.code}`);
            this._getLocationByIPv4Address().then((ipv4Location) => location.next(ipv4Location), (ipv4Error) => console.warn(ipv4Error));
        }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
        return location;
    }
    _getLocationByIPv4Address() {
        return new Promise((resolve, reject) => {
            this.httpClient
                .get('https://ipv4.myexternalip.com/json')
                .pipe(map((res) => res.ip), flatMap((ip) => this.httpClient.get('https://freegeoip.net/json/' + ip)), map((result) => {
                const ipv4Location = new Location();
                ipv4Location.address = result.city + ', ' + result.region_code + ' ' + result.zip_code + ', ' + result.country_code;
                ipv4Location.latitude = result.latitude;
                ipv4Location.longitude = result.longitude;
                return ipv4Location;
            }), take(1), timeout(10000))
                .subscribe((ipv4Location) => {
                resolve(ipv4Location);
            }, (error) => reject(error));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: GeocodingService, deps: [{ token: i1$1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: GeocodingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: GeocodingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.HttpClient }] });

class MapService {
    // http://leafletjs.com/reference-1.0.3.html
    constructor() {
        this.baseMaps = {
            OpenStreetMap: L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, ' +
                    'Tiles courtesy of <a href="https://hot.openstreetmap.org/" target="_blank">Humanitarian OpenStreetMap Team</a>'
            }),
            Esri: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}', {
                attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ, TomTom, Intermap, iPC, USGS, FAO, ' +
                    'NPS, NRCAN, GeoBase, Kadaster NL, Ordnance Survey, Esri Japan, METI, Esri China (Hong Kong), and the GIS User Community'
            }),
            CartoDB: L.tileLayer('http://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://cartodb.com/attributions">CartoDB</a>'
            })
        };
    }
    disableMouseEvent(elementId) {
        const element = document.getElementById(elementId);
        if (!!element) {
            L.DomEvent.disableClickPropagation(element);
            L.DomEvent.disableScrollPropagation(element);
        }
        else {
            console.warn(`element ${elementId} not found`);
        }
    }
    toggleCoverageLayer() {
        if (this.vtLayer) {
            this.map.removeLayer(this.vtLayer);
            delete this.vtLayer;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MapService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MapService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: MapService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class RouterHelperService {
    constructor(router, location) {
        this.router = router;
        this.location = location;
    }
    navigate(commands, replaceState, replaceStateCommands) {
        /*
        console.log('RouterHelperService.navigate - commands: ', commands);
        console.log('RouterHelperService.navigate - replaceState: ', replaceState);
        console.log('RouterHelperService.navigate - replaceStateCommands: ', replaceStateCommands);
        */
        // To avoid error: The requested path contains undefined segment at index 1
        const commandWithoutNullOrUndefined = commands.filter((item) => !!item);
        //You still need to navigate before replacing state, only use replace state if you want to pop the page from the browser history
        this.router.navigate(commandWithoutNullOrUndefined);
        if (replaceState) {
            if (!!replaceStateCommands) {
                this.location.replaceState(this.router.serializeUrl(this.router.createUrlTree(replaceStateCommands)));
            }
            else {
                this.location.replaceState(this.router.serializeUrl(this.router.createUrlTree(commands)));
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RouterHelperService, deps: [{ token: i1$2.Router }, { token: i2.Location }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RouterHelperService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: RouterHelperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$2.Router }, { type: i2.Location }] });

class UtilModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UtilModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: UtilModule, imports: [BrowserModule,
            HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UtilModule, providers: [I18nService, GeocodingService, MapService, RouterHelperService], imports: [BrowserModule,
            HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UtilModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        BrowserModule,
                        HttpClientModule,
                    ],
                    providers: [I18nService, GeocodingService, MapService, RouterHelperService],
                }]
        }] });

/*
 * Public API Surface of util
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BingLayer, GeocodingService, I18nService, Location, MapService, RouterHelperService, UtilModule };
